import { Injectable } from '@angular/core';

import { HisMapUtils } from '../../core/utils';
import { UserProperties } from '../../core/model/user-properties';
import { RoleSelectItem } from '../../core/model/role-select-item';

/**
 * provides authentication and authorisation information
 */
@Injectable({providedIn: 'root'})
export class HisAuthService {

    public currentAuthority: string;
    public orgunitId: number;
    public orgunitLid: number;
    public orgunitDefaultText: string;
    public rightsAndParameters: Map<string, Map<string, string>> = new Map();
    public username: string;
    public fullname: string;
    public isAnonymous: boolean;
    public roles: string[];
    public availableRoles: RoleSelectItem[] = []; // initialization required for karma tests
    public currentRole?: RoleSelectItem = undefined;
    public asi?: string = undefined;
    public personId: number;


    /**
     * used internally to initialize the service
     *
     * @param userinfo user-information as provided by AngularServlet
     */
    public init( userinfo: UserProperties ) {
        this.rightsAndParameters = HisMapUtils.mapFromObject( userinfo.rightsAndParameters, 2 );
        this.currentAuthority = userinfo.currentAuthority;
        this.orgunitId = userinfo.orgunitId;
        this.orgunitLid = userinfo.orgunitLid;
        this.orgunitDefaultText = userinfo.orgunitDefaultText;
        this.username = userinfo.username;
        this.fullname = userinfo.fullname;
        this.isAnonymous = userinfo.isAnonymous;
        this.roles = userinfo.roles;
        this.availableRoles = userinfo.availableRoles;
        this.asi = userinfo.asi;
        this.personId = userinfo.personId;


        // determine current role object; it is undefined if the user is not logged in
        for ( let role of userinfo.availableRoles ) {
            if ( role.authorityName === this.currentAuthority ) {
                this.currentRole = role;
                break;
            }
        }
        console.log(
            'username = ' + this.username
            + ', fullname = ' + this.fullname
            + ', personId = ' + this.personId
            + ', isAnonymous = ' + this.isAnonymous
            + ', currentRole = ' + this.currentRole );
        console.log( 'rightsAndParameters: ', this.rightsAndParameters );
    }

    /**
     * does the user have all required authorities?
     *
     * @param requiredAuthorities list of required rights
     */
    public hasAll( ...requiredAuthorities: string[] ): boolean {
        for ( let requiredAuthority of requiredAuthorities ) {
            if ( !this.rightsAndParameters.has( requiredAuthority ) ) {
                return false;
            }
        }
        return true;
    }

    /**
     * does the user have all required authorities?
     *
     * @param requiredAuthorities list of required rights
     */
    // Angular templates to not support ...-spread operator
    public hasAllArray(requiredAuthorities: string[] ): boolean {
        return this.hasAll(...requiredAuthorities);
    }

    /**
     * does the user have at least one of the required authorities?
     *
     * @param requiredAuthorities list of required rights
     */
    public hasAny( ...requiredAuthorities: string[] ): boolean {
        for ( let requiredAuthority of requiredAuthorities ) {
            if ( this.rightsAndParameters.has( requiredAuthority ) ) {
                return true;
            }
        }
        return false;
    }

    /**
     * does the user have at least one of the required authorities?
     *
     * @param requiredAuthorities list of required rights
     */
    // Angular templates to not support ...-spread operator
    public hasAnyArray( requiredAuthorities: string[] ): boolean {
        return this.hasAny(...requiredAuthorities);
    }

    /**
     * gets the value of a rightsparameter.
     *
     * @param right name of right
     * @param parameter name of parameter
     * @return rightsparameter value or <code>null</code>
     */
    public getRightsParameterValue( right: string, parameter: string ): string {
        let params = this.rightsAndParameters.get( right );
        if ( !params ) {
            return null;
        }
        return params.get( parameter );
    }
}
